import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
	loginThunk,
	registerThunk,
	refreshTokenThunk,
	registerRequestThunk,
	validateOtpThunk,
} from "../thunks/authThunks";
import {
	saveToStorage,
	removeFromStorage,
	getFromStorage,
} from "@utils/storage";

export interface User {
	id: number;
	uuid: string;
	username: string;
	email: string;
	phone: string;
	role: string;
	role_id: number;
	company_id: number;
	verified: number;
	active: number;
	created_at: string;
	updated_at: string;
}

interface AuthState {
	data: {
		access_token: string | null;
		refresh_token: string | null;
		exp: number | null;
		user: User | null;
	};
	isAuthenticated: boolean;
	status: "idle" | "loading" | "succeeded" | "failed";
	error: string | null;
}

const initialState: AuthState = {
	data: {
		access_token: getFromStorage<string>("access_token") || null,
		refresh_token: getFromStorage<string>("refresh_token") || null,
		exp: getFromStorage<number>("exp") || null,
		user: getFromStorage<User>("user") || null,
	},
	isAuthenticated: !!getFromStorage<string>("access_token"),
	status: "idle",
	error: null,
};

const authSlice = createSlice({
	name: "auth",
	initialState,
	reducers: {
		setAuthData: (state, action: PayloadAction<AuthState["data"]>) => {
			const { access_token, refresh_token, exp, user } = action.payload;
			state.data.access_token = access_token;
			state.data.refresh_token = refresh_token;
			state.data.exp = exp;
			state.data.user = user;
			state.isAuthenticated = true;
			// persist
			saveToStorage("access_token", access_token!);
			saveToStorage("refresh_token", refresh_token!);
			saveToStorage("user", user!);
			if (exp !== null && exp !== undefined) {
				saveToStorage("exp", exp);
			}
		},
		clearAuthState: (state) => {
			state.data.access_token = null;
			state.data.refresh_token = null;
			state.data.exp = null;
			state.data.user = null;
			state.isAuthenticated = false;
			state.status = "idle";
			state.error = null;

			removeFromStorage("access_token");
			removeFromStorage("refresh_token");
			removeFromStorage("exp");
			removeFromStorage("user");
		},
		updateUserCompanyId: (state, action: PayloadAction<number>) => {
			if (state.data.user) {
				state.data.user.company_id = action.payload;

				saveToStorage("user", state.data.user);
			}
		},
	},
	extraReducers: (builder) => {
		// Login Thunk
		builder.addCase(loginThunk.pending, (state) => {
			state.status = "loading";
		});
		builder.addCase(
			loginThunk.fulfilled,
			(state, action: PayloadAction<AuthState["data"]>) => {
				const { access_token, refresh_token, user, exp } = action.payload;
				console.log("Access token:", access_token);

				state.data.access_token = access_token;
				state.data.refresh_token = refresh_token;
				state.data.user = user;
				state.data.exp = exp;
				state.isAuthenticated = true;
				state.status = "succeeded";

				saveToStorage("access_token", access_token);
				saveToStorage("refresh_token", refresh_token);
				saveToStorage("user", user);
				saveToStorage("exp", exp); // ✅ also persist exp
			},
		);
		builder.addCase(loginThunk.rejected, (state, action) => {
			state.status = "failed";
			state.error = action.payload as string;
		});

		// Register Thunk
		builder.addCase(registerThunk.pending, (state) => {
			state.status = "loading";
		});
		builder.addCase(
			registerThunk.fulfilled,
			(state, action: PayloadAction<{ data: AuthState["data"] }>) => {
				const { access_token, refresh_token, user, exp } = action.payload.data;
				state.data.access_token = access_token;
				state.data.refresh_token = refresh_token;
				state.data.user = user;
				state.data.exp = exp;
				state.isAuthenticated = true;
				state.status = "succeeded";

				saveToStorage("access_token", access_token);
				saveToStorage("refresh_token", refresh_token);
				saveToStorage("user", user);
			},
		);
		builder.addCase(registerThunk.rejected, (state, action) => {
			state.status = "failed";
			state.error = action.payload as string;
		});

		// Refresh Token Thunk
		builder.addCase(refreshTokenThunk.pending, (state) => {
			state.status = "loading";
		});
		builder.addCase(
			refreshTokenThunk.fulfilled,
			(state, action: PayloadAction<string>) => {
				state.data.access_token = action.payload;
				state.status = "succeeded";

				saveToStorage("access_token", action.payload);
			},
		);
		builder.addCase(refreshTokenThunk.rejected, (state, action) => {
			state.status = "failed";
			state.error = action.payload as string;
		});

		// Register Request Thunk
		builder.addCase(registerRequestThunk.pending, (state) => {
			state.status = "loading";
		});
		builder.addCase(registerRequestThunk.fulfilled, (state) => {
			state.status = "succeeded";
		});
		builder.addCase(registerRequestThunk.rejected, (state, action) => {
			state.status = "failed";
			state.error = action.payload as string;
		});

		// Validate OTP Thunk
		builder.addCase(validateOtpThunk.pending, (state) => {
			state.status = "loading";
		});
		builder.addCase(validateOtpThunk.fulfilled, (state) => {
			state.status = "succeeded";
		});
		builder.addCase(validateOtpThunk.rejected, (state, action) => {
			state.status = "failed";
			state.error = action.payload as string;
		});
	},
});

export const { clearAuthState, updateUserCompanyId, setAuthData } =
	authSlice.actions;
export default authSlice.reducer;
